import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiService } from '@shared/services/api/ApiService';
import { socketService } from '../services/SocketService';

// 临时的本地store,替代shared包
export interface User {
  id: string;
  phone?: string;
  email?: string;
  nickname?: string;
  avatar?: string;
  grade?: string;
  bio?: string;
  phone_verified?: boolean;
  email_verified?: boolean;
  wechat_openid?: string | null; // 微信 openid，未关联时为 null
  token?: string;
  role?: 'user' | 'teacher' | 'admin' | 'super_admin' | 'parent'; // 用户角色
  
  // 用户信息字段
  gender?: number;         // 0=未知, 1=男, 2=女
  country?: string;        // 国家代码，如CN, US等
  province?: string;       // 省份
  city?: string;          // 城市
  language?: string;      // 语言代码，如zh_CN, en_US等
  
  // AI 头像字段
  ai_avatar_gemini?: string;   // Gemini AI 头像路径
  ai_avatar_deepseek?: string; // DeepSeek AI 头像路径
  
  // 时间字段
  lastLoginAt?: Date;      // 最后登录时间
  createdAt?: Date;        // 创建时间
  updatedAt?: Date;        // 更新时间
}

export interface LoginData {
  contact?: string;  // 邮箱或手机号的统一输入字段
  phone?: string;
  email?: string;
  password: string;
}

export interface LoginWithCodeData {
  contact?: string;  // 邮箱或手机号的统一输入字段
  phone?: string;
  email?: string;
  verificationCode: string;
}

export interface RegisterData {
  phone?: string;
  email?: string;
  password: string;
  nickname: string;
  verificationCode: string;
}

export interface SendVerificationCodeData {
  contact?: string;  // 邮箱或手机号的统一输入字段
  phone?: string;
  email?: string;
  type: 'register' | 'login' | 'reset' | 'profile_verify';
}

export interface ResetPasswordData {
  phone?: string;
  email?: string;
  verificationCode: string;
  newPassword: string;
}

export interface UpdateProfileData {
  nickname?: string;
  grade?: string;
  bio?: string;
  country?: string;
  province?: string;
  city?: string;
  language?: string;
  gender?: number;
}

export interface VerifyContactData {
  phone?: string;
  email?: string;
  verificationCode: string;
  verificationMethod?: 'email' | 'phone'; // 用哪种方式验证身份
  type: 'register' | 'profile_verify';
}

export type LoadingState = 'idle' | 'loading' | 'success' | 'error';

export interface AppStore {
  // Auth state
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loginState: LoadingState;
  registerState: LoadingState;
  verificationState: LoadingState;
  resetPasswordState: LoadingState;
  profileState: LoadingState;

  // Auth actions
  setUser: (user: User) => void;
  setTokens: (token: string, refreshToken: string) => void;
  logout: () => void;
  setLoginState: (state: LoadingState) => void;
  setRegisterState: (state: LoadingState) => void;
  setVerificationState: (state: LoadingState) => void;
  setResetPasswordState: (state: LoadingState) => void;
  login: (data: LoginData) => Promise<void>;
  loginWithCode: (data: LoginWithCodeData) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  sendVerificationCode: (data: SendVerificationCodeData) => Promise<boolean>;
  resetPassword: (data: ResetPasswordData) => Promise<void>;
  updateProfile: (data: UpdateProfileData) => Promise<void>;
  verifyAndUpdateContact: (data: VerifyContactData) => Promise<boolean>;
  getCurrentUser: () => Promise<void>;
  initializeAuth: () => void;
}

export const useStore = create<AppStore>()(
  devtools(
    (set) => ({
      // Initial state
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      loginState: 'idle',
      registerState: 'idle',
      verificationState: 'idle',
      resetPasswordState: 'idle',
      profileState: 'idle',

      // Actions
      setUser: (user) => set({ user, isAuthenticated: true }),

      setTokens: (token, refreshToken) => {
        set({ token, refreshToken, isAuthenticated: true });
        // 持久化存储
        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_refresh_token', refreshToken);
        // 设置ApiService的token
        apiService.setToken(token);
      },

      logout: async () => {
        try {
          // 1. 调用服务器端 logout API（清理 Socket 会话）
          try {
            await apiService.post('/auth/logout');
          } catch (error) {
            // API 调用失败也继续清理本地状态
            console.warn('Logout API call failed, continuing with local cleanup', error);
          }

          // 2. 断开 Socket 连接
          try {
            socketService.disconnect();
          } catch (error) {
            console.warn('Failed to disconnect socket', error);
          }

          // 3. 清除本地状态
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            loginState: 'idle',
            registerState: 'idle',
            verificationState: 'idle',
            resetPasswordState: 'idle',
            profileState: 'idle'
          });

          // 4. 清除本地存储
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_refresh_token');
          localStorage.removeItem('auth_user');

          // 5. 清除 ApiService 的 token
          apiService.clearToken();

          // 6. 跳转到登录页
          window.location.href = '/login';
        } catch (error) {
          console.error('Logout error:', error);
          // 即使出错也要清理本地状态
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false
          });
          localStorage.clear();
          window.location.href = '/login';
        }
      },

      setLoginState: (loginState) => set({ loginState }),
      setRegisterState: (registerState) => set({ registerState }),
      setVerificationState: (verificationState) => set({ verificationState }),
      setResetPasswordState: (resetPasswordState) => set({ resetPasswordState }),

      login: async (data) => {
        set({ loginState: 'loading' });

        try {
          const response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });

          const result = await response.json();

          if (result.success) {
            const { user: userData, tokens } = result.data;
            const { accessToken, refreshToken } = tokens;
            // 映射后端的 avatar_url 到前端的 avatar
            const user = {
              ...userData,
              avatar: userData.avatar_url || userData.avatar,
            };
            set({
              user,
              token: accessToken,
              refreshToken,
              isAuthenticated: true,
              loginState: 'success',
            });
            // 持久化存储
            localStorage.setItem('auth_token', accessToken);
            localStorage.setItem('auth_refresh_token', refreshToken);
            localStorage.setItem('auth_user', JSON.stringify(user));
            // 设置ApiService的token
            apiService.setToken(accessToken);
          } else {
            set({ loginState: 'error' });
            throw new Error(result.error?.message || result.message || '登录失败');
          }
        } catch (error) {
          set({ loginState: 'error' });
          throw error;
        }
      },

      loginWithCode: async (data) => {
        set({ loginState: 'loading' });

        try {
          const response = await fetch('/api/auth/login-with-code', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });

          const result = await response.json();

          if (result.success) {
            const { user: userData, tokens } = result.data;
            const { accessToken, refreshToken } = tokens;
            // 映射后端的 avatar_url 到前端的 avatar
            const user = {
              ...userData,
              avatar: userData.avatar_url || userData.avatar,
            };

            set({
              user,
              token: accessToken,
              refreshToken,
              isAuthenticated: true,
              loginState: 'success',
            });
            // 持久化存储
            localStorage.setItem('auth_token', accessToken);
            localStorage.setItem('auth_refresh_token', refreshToken);
            localStorage.setItem('auth_user', JSON.stringify(user));
            // 设置ApiService的token
            apiService.setToken(accessToken);
          } else {
            set({ loginState: 'error' });
            throw new Error(result.error?.message || result.message || '登录失败');
          }
        } catch (error) {
          set({ loginState: 'error' });
          throw error;
        }
      },

      register: async (data) => {
        set({ registerState: 'loading' });

        try {
          const response = await fetch('/api/auth/register', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });

          const result = await response.json();

          if (result.success) {
            const { user: userData, tokens } = result.data;
            const { accessToken, refreshToken } = tokens;
            // 映射后端的 avatar_url 到前端的 avatar
            const user = {
              ...userData,
              avatar: userData.avatar_url || userData.avatar,
            };
            set({
              user,
              token: accessToken,
              refreshToken,
              isAuthenticated: true,
              registerState: 'success',
            });
            // 持久化存储
            localStorage.setItem('auth_token', accessToken);
            localStorage.setItem('auth_refresh_token', refreshToken);
            localStorage.setItem('auth_user', JSON.stringify(user));
          } else {
            set({ registerState: 'error' });
            throw new Error(result.error?.message || result.message || '注册失败');
          }
        } catch (error) {
          set({ registerState: 'error' });
          throw error;
        }
      },

      sendVerificationCode: async (data) => {
        set({ verificationState: 'loading' });

        try {
          const response = await fetch('/api/auth/send-verification-code', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });

          const result = await response.json();

          if (result.success) {
            set({ verificationState: 'success' });
            return true;
          } else {
            set({ verificationState: 'error' });
            throw new Error(result.error?.message || result.message || '发送验证码失败');
          }
        } catch (error) {
          set({ verificationState: 'error' });
          throw error;
        }
      },

      resetPassword: async (data) => {
        set({ resetPasswordState: 'loading' });

        try {
          const response = await fetch('/api/auth/reset-password', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });

          const result = await response.json();

          if (result.success) {
            set({ resetPasswordState: 'success' });
          } else {
            set({ resetPasswordState: 'error' });
            throw new Error(result.error?.message || result.message || '重置密码失败');
          }
        } catch (error) {
          set({ resetPasswordState: 'error' });
          throw error;
        }
      },

      updateProfile: async (data) => {
        set({ profileState: 'loading' });

        try {
          const token = localStorage.getItem('auth_token');
          const response = await fetch('/api/auth/profile', {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
          });

          const result = await response.json();

          if (result.success) {
            const updatedUserData = result.data.user;
            // 映射后端的 avatar_url 到前端的 avatar
            const updatedUser = {
              ...updatedUserData,
              avatar: updatedUserData.avatar_url || updatedUserData.avatar,
            };
            set((state) => ({
              user: { ...state.user, ...updatedUser },
              profileState: 'success',
            }));
            // 更新本地存储
            const currentUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
            localStorage.setItem('auth_user', JSON.stringify({ ...currentUser, ...updatedUser }));
          } else {
            set({ profileState: 'error' });
            throw new Error(result.error?.message || result.message || '更新失败');
          }
        } catch (error) {
          set({ profileState: 'error' });
          throw error;
        }
      },

      verifyAndUpdateContact: async (data) => {
        try {
          const token = localStorage.getItem('auth_token');
          console.log('Sending verify contact request:', { ...data, verificationCode: '***' });

          const response = await fetch('/api/auth/verify-contact', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
          });

          console.log('Response status:', response.status);
          const result = await response.json();
          console.log('Response data:', result);

          if (result.success) {
            const updatedUserData = result.data.user;
            // 映射后端的 avatar_url 到前端的 avatar
            const updatedUser = {
              ...updatedUserData,
              avatar: updatedUserData.avatar_url || updatedUserData.avatar,
            };
            console.log('Updating user in store:', updatedUser);
            set((state) => ({
              user: { ...state.user, ...updatedUser },
            }));
            // 更新本地存储
            const currentUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
            localStorage.setItem('auth_user', JSON.stringify({ ...currentUser, ...updatedUser }));
            return true;
          } else {
            console.error('Verification failed:', result);
            throw new Error(result.error?.message || result.message || '验证失败');
          }
        } catch (error) {
          console.error('Verify contact error:', error);
          throw error;
        }
      },

      getCurrentUser: async () => {
        try {
          console.log('=== getCurrentUser called ===');
          const token = localStorage.getItem('auth_token');
          if (!token) {
            throw new Error('No token found');
          }

          const response = await fetch('/api/auth/profile', {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          const result = await response.json();

          if (result.success) {
            const userData = result.data.user;
            // 映射后端的 avatar_url 到前端的 avatar
            const user = {
              ...userData,
              avatar: userData.avatar_url || userData.avatar,
            };
            set({ user });
            // 更新本地存储
            localStorage.setItem('auth_user', JSON.stringify(user));
          } else {
            throw new Error(result.message || '获取用户信息失败');
          }
        } catch (error) {
          console.error('Get current user error:', error);
          throw error;
        }
      },

      initializeAuth: () => {
        const token = localStorage.getItem('auth_token');
        const refreshToken = localStorage.getItem('auth_refresh_token');
        const userStr = localStorage.getItem('auth_user');

        if (token && refreshToken && userStr) {
          try {
            const user = JSON.parse(userStr);
            set({
              user,
              token,
              refreshToken,
              isAuthenticated: true,
            });
            // 设置ApiService的token
            apiService.setToken(token);
          } catch (error) {
            console.error('Failed to parse stored user data:', error);
            // 清除无效数据
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_refresh_token');
            localStorage.removeItem('auth_user');
          }
        }
      },
    }),
    {
      name: 'ai-math-tutor-store',
    }
  )
);